"use client";

import { ArrowUpRight, Lightning, PlayCircle, Sparkle } from "@phosphor-icons/react";
import { AnimatePresence, motion, useScroll, useSpring, useTransform } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { cultureCardImages } from "@/components/home/data";
import { cn } from "@/lib/utils";
import type { Locale } from "@/i18n";

export type AtmosphereTranslations = {
  moreThan: string;
  aGathering: string;
  description: string;
  marketplaceDominance: string;
  divinePurpose: string;
  theCulture: string;
  authenticFellowship: string;
  cultureDescription: string;
  q2Badge: string;
  schoolOfDestiny: string;
  schoolDescription: string;
  explore: string;
  live: string;
  watchOnline: string;
  watchDescription: string;
  goToWatch: string;
  theExpression: string;
};

const defaultTranslations: AtmosphereTranslations = {
  moreThan: "More Than",
  aGathering: "A Gathering",
  description: "We are a generation set on fire for God. Beyond our weekly services, we build lives of impact, excellence, and authentic community across Canada.",
  marketplaceDominance: "Marketplace\nDominance",
  divinePurpose: "Divine\nPurpose",
  theCulture: "The Culture",
  authenticFellowship: "Authentic Fellowship",
  cultureDescription: "Find your tribe in a city that can feel large. Real relationships, real growth.",
  q2Badge: "Q2 2026",
  schoolOfDestiny: "School of Destiny",
  schoolDescription: "Enrichment program empowering Canadian youth for marketplace excellence.",
  explore: "Explore",
  live: "Live",
  watchOnline: "Watch Online",
  watchDescription: "Catch the latest messages, worship, and special gatherings.",
  goToWatch: "Go to Watch",
  theExpression: "The Expression",
};

function ParallaxImage({
  src,
  alt,
  className = "",
  imageClassName = "",
  parallax = true,
}: {
  src: string;
  alt: string;
  className?: string;
  imageClassName?: string;
  parallax?: boolean;
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);
  const smoothY = useSpring(y, { stiffness: 100, damping: 30 });

  return (
    <div ref={ref} className={cn("overflow-hidden relative h-full w-full", className)}>
      <motion.div
        style={parallax ? { y: smoothY } : undefined}
        className={cn("w-full relative", parallax ? "h-[130%] -top-[15%]" : "h-full top-0")}
      >
        <Image
          src={src}
          alt={alt}
          fill
          className={cn("object-cover", imageClassName)}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      </motion.div>
    </div>
  );
}

function RotatingParallaxImage({
  sources,
  alt,
  className = "",
  intervalMs = 5500,
}: {
  sources: string[];
  alt: string;
  className?: string;
  intervalMs?: number;
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);
  const smoothY = useSpring(y, { stiffness: 100, damping: 30 });
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (sources.length <= 1) return;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % sources.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, sources]);

  const src = sources[index] ?? sources[0];

  return (
    <div ref={ref} className={cn("overflow-hidden relative h-full w-full", className)}>
      <motion.div style={{ y: smoothY }} className="h-[130%] w-full relative -top-[15%]">
        <div className="absolute inset-0">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={src}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <Image
                src={src}
                alt={alt}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

type AtmosphereBentoGridProps = {
  locale?: Locale;
  translations?: AtmosphereTranslations;
};

export const AtmosphereBentoGrid = ({
  locale = "en",
  translations = defaultTranslations,
}: AtmosphereBentoGridProps) => {
  const t = translations;
  return (
    <section id="about" className="py-32 bg-white dark:bg-black relative overflow-hidden transition-colors">
      <div className="max-w-[1600px] mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-end justify-between mb-20 gap-8">
          <div className="max-w-2xl">
            <h2 className="text-5xl md:text-7xl lg:text-9xl font-serif text-black dark:text-white leading-[0.9] tracking-tighter">
              {t.moreThan} <br />
              <span className="italic text-brand-red underline decoration-1 underline-offset-8">{t.aGathering}</span>
            </h2>
            <p className="text-xl text-zinc-600 dark:text-zinc-400 font-light leading-relaxed mt-8">
              {t.description}
            </p>
          </div>
          <div className="hidden lg:flex gap-4">
            <div className="p-6 bg-white dark:bg-zinc-900 rounded-3xl border border-zinc-200 dark:border-white/10 flex items-center gap-4 shadow-sm">
              <div className="p-3 bg-brand-red/10 rounded-xl text-brand-red">
                <Lightning weight="fill" size={24} />
              </div>
              <span className="text-xs font-black uppercase tracking-widest leading-none text-black dark:text-white whitespace-pre-line">
                {t.marketplaceDominance}
              </span>
            </div>
            <div className="p-6 bg-white dark:bg-zinc-900 rounded-3xl border border-zinc-200 dark:border-white/10 flex items-center gap-4 shadow-sm">
              <div className="p-3 bg-brand-red/10 rounded-xl text-brand-red">
                <Sparkle weight="fill" size={24} />
              </div>
              <span className="text-xs font-black uppercase tracking-widest leading-none text-black dark:text-white whitespace-pre-line">
                {t.divinePurpose}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:auto-rows-[500px] lg:auto-rows-[600px]">
          {/* Main Featured */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="col-span-12 lg:col-span-6 min-h-[400px] relative rounded-[3rem] overflow-hidden group shadow-2xl"
          >
            <RotatingParallaxImage sources={cultureCardImages} alt="Community Life" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-12 left-12 text-white">
              <span className="text-[10px] uppercase tracking-[0.4em] font-bold mb-2 block text-brand-gold drop-shadow-[0_2px_10px_rgba(255,220,140,0.9)]">
                {t.theCulture}
              </span>
              <h3 className="text-4xl font-serif italic mb-4 leading-none">{t.authenticFellowship}</h3>
              <p className="text-white/60 max-w-sm font-light">
                {t.cultureDescription}
              </p>
            </div>
          </motion.div>

          {/* Right Stacks */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3 flex flex-col gap-6">
            <div className="md:flex-1 min-h-[300px] relative rounded-[3rem] overflow-hidden group shadow-xl">
              <ParallaxImage src="/ayac/_DSC2769.JPG" alt="Worship" />
              <div className="absolute inset-0 bg-brand-red/10 group-hover:bg-transparent transition-colors" />
            </div>
            <Link
              href={`/${locale}/academy`}
              className="md:flex-1 min-h-[300px] bg-brand-gold rounded-[3rem] p-10 flex flex-col justify-between group cursor-pointer hover:bg-black dark:hover:bg-zinc-800 hover:text-white transition-all duration-500 shadow-xl relative overflow-hidden"
            >
              {/* Status Badge */}
              <div className="absolute top-4 right-4 px-3 py-1 bg-black/10 group-hover:bg-white/20 backdrop-blur-sm rounded-full border border-black/20 group-hover:border-white/30 transition-colors">
                <span className="text-[8px] font-black uppercase tracking-widest text-black group-hover:text-white transition-colors">
                  {t.q2Badge}
                </span>
              </div>

              <Lightning weight="duotone" size={48} className="text-black group-hover:text-white transition-colors" />
              <div>
                <h3 className="text-3xl font-serif font-bold italic leading-none mb-2">{t.schoolOfDestiny}</h3>
                <p className="text-sm opacity-75 font-light mb-4 text-black group-hover:text-white/60 transition-colors">
                  {t.schoolDescription}
                </p>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-black/75 dark:text-white/75 group-hover:text-white transition-colors">
                  <span>{t.explore}</span>
                  <ArrowUpRight weight="bold" className="w-3 h-3" />
                </div>
              </div>
            </Link>
          </div>

          <div className="col-span-12 md:col-span-6 lg:col-span-3 flex flex-col gap-6">
            <Link
              href={`/${locale}/watch`}
              className="md:flex-1 min-h-[300px] bg-black text-white rounded-[3rem] p-10 flex flex-col justify-between shadow-xl border border-white/10 hover:border-brand-red hover:text-brand-gold transition-all duration-500"
            >
              <div className="flex items-center justify-between">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
                  <PlayCircle weight="fill" size={32} className="text-brand-gold" />
                </div>
                <span className="text-[10px] uppercase tracking-[0.4em] font-black text-white/60">{t.live}</span>
              </div>
              <div>
                <h3 className="text-3xl font-serif italic mb-3 leading-none">{t.watchOnline}</h3>
                <p className="text-sm text-white/70 font-light mb-6">
                  {t.watchDescription}
                </p>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/70">
                  <span>{t.goToWatch}</span>
                  <ArrowUpRight weight="bold" className="w-3 h-3" />
                </div>
              </div>
            </Link>
            <div className="md:flex-1 min-h-[300px] relative rounded-[3rem] overflow-hidden group shadow-xl bg-black aspect-[4/3] sm:aspect-auto">
              <ParallaxImage
                src="/choir/_DSC5752.JPG"
                alt="Choir"
                parallax={false}
                imageClassName="object-contain sm:object-cover"
              />
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
                <span className="text-white font-serif text-3xl italic">{t.theExpression}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
